import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, WifiOff, Users, Sparkles, ChevronRight } from "lucide-react";
import api from "../../lib/api";

/* Spec §9 — Master Readiness Dashboard tile.
   One card combining: lifecycle_state · readiness score · Inner-Circle recipients ·
   Offline-Pack status · next-best action for this trip. */

const STATE_TONE = {
  dream:     { bg: "bg-slate-100 text-slate-700",     label: "Dream" },
  planning:  { bg: "bg-blue-100 text-blue-700",       label: "Planning" },
  booked:    { bg: "bg-indigo-100 text-indigo-700",   label: "Booked" },
  preparing: { bg: "bg-amber-100 text-amber-800",     label: "Preparing" },
  traveling: { bg: "bg-emerald-100 text-emerald-800", label: "Traveling" },
  completed: { bg: "bg-teal-100 text-teal-800",       label: "Completed" },
  archived:  { bg: "bg-slate-200 text-slate-600",     label: "Archived" },
};

export default function ReadinessTile({ tripId }) {
  const [data, setData] = useState(null);
  const [circle, setCircle] = useState(null);
  const [offline, setOffline] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/trips/${tripId}/readiness`).then((r) => alive && setData(r.data)).catch(() => {});
    api.get(`/trips/${tripId}/offline-pack/status`).then((r) => alive && setOffline(r.data)).catch(() => {});
    api.get("/trusted-circle/members").then((r) => alive && setCircle((r.data.items || []).length)).catch(() => setCircle(0));
    return () => { alive = false; };
  }, [tripId]);

  if (!data) return null;

  const state = STATE_TONE[data.lifecycle_state] || STATE_TONE.planning;
  const score = data.score || 0;
  const nextAction = (() => {
    if (score < 100 && data.categories) {
      for (const c of data.categories) {
        for (const it of c.items) if (!it.done) return it.label;
      }
    }
    if (!circle) return "Add someone to your Inner Circle";
    if (!offline || !offline.devices || offline.devices.length === 0) return "Download the offline pack on your phone";
    return "You're all set — safe travels!";
  })();

  return (
    <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-blue-50/40 p-5" data-testid="readiness-tile">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-slate-500 mb-1">Trip Readiness · Spec §9</div>
          <div className="flex items-center gap-2">
            <span className={`text-[11px] font-800 uppercase tracking-wider rounded-full px-2.5 py-1 ${state.bg}`} data-testid="readiness-state">
              {state.label}
            </span>
            <span className="font-serif font-800 text-4xl text-slate-900">{score}%</span>
          </div>
          <div className="text-sm text-slate-600 mt-0.5">{data.attention} item{data.attention === 1 ? "" : "s"} need attention</div>
        </div>
        <div className="text-right shrink-0">
          <div className="w-14 h-14 rounded-full grid place-items-center bg-white border-2 border-blue-100 shadow-sm">
            {score >= 100 ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <AlertTriangle className="w-6 h-6 text-amber-600" />}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <Mini label="Inner Circle" value={circle ?? "—"} Icon={Users} to="/inner-circle" />
        <Mini label="Offline Pack"
          value={offline && offline.devices?.length > 0 ? (offline.devices.some((d) => d.stale) ? "Stale" : "Ready") : "Not set"}
          Icon={WifiOff} tone={offline?.devices?.some((d) => d.stale) ? "amber" : "green"} />
        <Mini label="Score" value={`${score}%`} Icon={Sparkles} />
      </div>

      <div className="mt-4 rounded-xl border border-blue-200 bg-white p-3 flex items-center gap-3" data-testid="readiness-next-action">
        <span className="text-[10px] font-800 uppercase tracking-widest text-blue-600 shrink-0">Next</span>
        <span className="text-sm text-slate-800 flex-1 min-w-0 truncate">{nextAction}</span>
        <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
      </div>
    </div>
  );
}

function Mini({ label, value, Icon, tone = "blue", to }) {
  const bg = tone === "amber" ? "text-amber-700" : tone === "green" ? "text-emerald-700" : "text-blue-700";
  const inner = (
    <div className="rounded-xl bg-white border border-slate-200 p-2.5 hover:border-blue-300 transition">
      <div className="flex items-center justify-center gap-1 text-slate-500"><Icon className="w-3.5 h-3.5" /><span className="text-[10px] uppercase tracking-widest font-700">{label}</span></div>
      <div className={`font-800 text-lg mt-0.5 ${bg}`}>{value}</div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}
